import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params//valid-params";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { getAdvBuilds } from "@ugg/shared/api/data-parser/champions/adv-builds";
import { useChampionRecBuild } from "@ugg/shared/api/requests/champions/adv-builds";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { ChampionProfilePagePlaceholder } from "@ugg/shared/components/Champions/ChampionProfilePagePlaceholder";
import { ChampionProfilePageError } from "@ugg/shared/components/Champions/ChampionProfilePageError";
import { normalizeRole } from "@ugg/shared/utils/role-helpers";
import { RuneSetList } from "./RuneSetList";
import { RuneSetPairs } from "./RuneSetPairs";

export function RuneSetsContainer() {
  const { championId, role } = useChampionProfileContext();
  const [viewAll, setViewAll] = useState(false);
  const [runeSort, setRuneSort] = useState<["pick" | "win", boolean]>(["pick", true]);

  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.RUNE_SETS, queryParams, true);
  const { data, loading, error } = useChampionRecBuild(championId, CHAMPION_PAGES.RUNE_SETS, {
    params: validatedQueryParams,
    ssr: true,
  });
  const recommendedBuild = getAdvBuilds(data, { ...validatedQueryParams, role: normalizeRole(role || "") });
  const { keystone_pairs = [] } = recommendedBuild || {};

  const patch = validatedQueryParams.patch;

  const setSortBy = (sortBy: (typeof runeSort)[0]) => {
    setRuneSort([sortBy, runeSort[0] === sortBy ? !runeSort[1] : true]);
  };

  const getDefaultPair = () => {
    const pairs = keystone_pairs;
    if (pairs.length > 0) {
      let defaultPairIndex = 0;
      pairs.forEach((pair, index) => {
        if (pairs[defaultPairIndex].matches < pair.matches) {
          defaultPairIndex = index;
        }
      });

      return [pairs[defaultPairIndex].keystone_id, pairs[defaultPairIndex].sub_style] as const;
    }
    return null;
  };
  // [keystone, sub_style]
  const [selectedRunePair, setSelectedRunePair] = useState<readonly [number, number] | null>(getDefaultPair());

  useEffect(() => {
    setSelectedRunePair(getDefaultPair());
  }, [keystone_pairs]);

  const totalMatches = keystone_pairs.reduce((acc, curr) => acc + curr.matches, 0);
  const { pairs, sets } = useMemo(() => {
    const sets = keystone_pairs
      .map((el) => el.runes)
      .flat()
      .filter((runes) => {
        if (viewAll) {
          return runes.matches / totalMatches >= 0.01;
        }

        return runes.keystone_id === selectedRunePair?.[0] && runes.sub_style === selectedRunePair?.[1];
      })
      .sort((a, b) => {
        const [sortBy, dir] = runeSort;
        if (sortBy === "pick") {
          return dir ? b.matches - a.matches : a.matches - b.matches;
        } else if (sortBy === "win") {
          return dir ? b.wins / b.matches - a.wins / a.matches : a.wins / a.matches - b.wins / b.matches;
        }
        return 0;
      });

    return {
      pairs: [...keystone_pairs].sort((a, b) => {
        return b.matches - a.matches;
      }),
      sets,
    };
  }, [keystone_pairs, viewAll, selectedRunePair, runeSort]);

  if (loading) {
    return <ChampionProfilePagePlaceholder className="w-full" />;
  } else if (error || !keystone_pairs || keystone_pairs.length === 0) {
    return <ChampionProfilePageError className="w-full" />;
  }

  return (
    <div className="flex gap-[12px] w-full">
      <div className="flex-none w-[280px]">
        <RuneSetPairs
          data={pairs}
          patch={patch}
          totalMatches={totalMatches}
          viewAll={viewAll}
          setViewAll={setViewAll}
          selectedRunePair={selectedRunePair}
          setSelectedRunePair={setSelectedRunePair}
        />
      </div>
      <div className="flex-1">
        <RuneSetList
          sets={sets}
          viewAll={viewAll}
          sort={runeSort}
          setSort={setSortBy}
          patch={patch}
          totalMatches={totalMatches}
        />
      </div>
    </div>
  );
}
